import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2, ArrowLeft, ArrowUp, ArrowDown, X } from "lucide-react";

export default function ConfiguracionDescuentos() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ nombre: "", porcentaje: "" });
  const [editId, setEditId] = useState(null);

  const load = async () => { const { data } = await api.get("/descuentos"); setRows(data || []); };
  useEffect(() => { load(); }, []);

  const guardar = async () => {
    if (!form.nombre) return toast.error("Nombre requerido");
    try {
      if (editId) {
        const original = rows.find((r) => r.id === editId);
        await api.put(`/descuentos/${editId}`, { nombre: form.nombre, porcentaje: Number(form.porcentaje) || 0, activo: original ? original.activo : true });
        toast.success("Descuento actualizado");
      } else {
        await api.post("/descuentos", { nombre: form.nombre, porcentaje: Number(form.porcentaje) || 0, activo: true });
        toast.success("Descuento agregado");
      }
      setForm({ nombre: "", porcentaje: "" });
      setEditId(null);
      load();
    } catch (err) { toast.error(`Error: ${err?.response?.data?.detail || err.message}`); }
  };
  const startEdit = (d) => { setEditId(d.id); setForm({ nombre: d.nombre, porcentaje: String(d.porcentaje ?? "") }); };
  const cancelEdit = () => { setEditId(null); setForm({ nombre: "", porcentaje: "" }); };
  const toggle = async (d) => {
    try { await api.put(`/descuentos/${d.id}`, { nombre: d.nombre, porcentaje: d.porcentaje, activo: !d.activo }); load(); }
    catch (err) { toast.error(`Error: ${err?.response?.data?.detail || err.message}`); }
  };
  const eliminar = async (id) => {
    try { await api.delete(`/descuentos/${id}`); setRows((p) => p.filter((r) => r.id !== id)); toast.success("Eliminado"); if (editId === id) cancelEdit(); }
    catch (err) { toast.error(`Error: ${err?.response?.data?.detail || err.message}`); }
  };
  const move = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= rows.length) return;
    const newRows = [...rows];
    [newRows[index], newRows[target]] = [newRows[target], newRows[index]];
    setRows(newRows);
    try { await api.post("/descuentos/reorder", { ids: newRows.map((r) => r.id) }); }
    catch (err) { toast.error(`Error: ${err?.response?.data?.detail || err.message}`); load(); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="descuentos-page">
      <Button variant="ghost" size="sm" onClick={() => navigate("/configuracion")}><ArrowLeft className="h-4 w-4 mr-2" /> Configuración</Button>
      <div><h1 className="text-3xl font-bold tracking-tight">Descuentos</h1><p className="text-muted-foreground mt-2">Políticas de descuento del sistema.</p></div>
      <Card><CardContent className="pt-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div className="space-y-2 md:col-span-1"><Label>Nombre</Label><Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="desc-nombre" /></div>
          <div className="space-y-2"><Label>Porcentaje</Label><Input type="number" value={form.porcentaje} onChange={(e) => setForm({ ...form, porcentaje: e.target.value })} data-testid="desc-porcentaje" /></div>
          <div className="flex gap-2">
            <Button onClick={guardar} data-testid="desc-add" className="flex-1">{editId ? <><Pencil className="h-4 w-4 mr-2" /> Guardar cambios</> : <><Plus className="h-4 w-4 mr-2" /> Agregar</>}</Button>
            {editId && <Button variant="outline" onClick={cancelEdit} data-testid="desc-cancel"><X className="h-4 w-4" /></Button>}
          </div>
        </div>
        <div className="border rounded-md">
          <Table>
            <TableHeader><TableRow><TableHead>Nombre</TableHead><TableHead>Porcentaje</TableHead><TableHead>Estado</TableHead><TableHead className="w-24 text-right">Acciones</TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.length === 0 ? <TableRow><TableCell colSpan={4} className="text-center h-20 text-muted-foreground">Sin descuentos.</TableCell></TableRow>
                : rows.map((d, i) => (
                  <TableRow key={d.id} className={editId === d.id ? "bg-muted/50" : ""}>
                    <TableCell className="font-medium">{d.nombre}</TableCell>
                    <TableCell>{d.porcentaje}%</TableCell>
                    <TableCell><Badge className={d.activo ? "bg-emerald-500 cursor-pointer" : "bg-slate-400 cursor-pointer"} onClick={() => toggle(d)}>{d.activo ? "Activo" : "Inactivo"}</Badge></TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" title="Subir" disabled={i === 0} onClick={() => move(i, -1)} data-testid={`desc-up-${d.id}`}><ArrowUp className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" title="Bajar" disabled={i === rows.length - 1} onClick={() => move(i, 1)} data-testid={`desc-down-${d.id}`}><ArrowDown className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" title="Editar" onClick={() => startEdit(d)} data-testid={`desc-edit-${d.id}`}><Pencil className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" className="text-destructive" title="Eliminar" onClick={() => eliminar(d.id)} data-testid={`desc-del-${d.id}`}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>
      </CardContent></Card>
    </div>
  );
}
